import { useMemo, useState } from 'react'

import { alpha } from '@mui/material/styles'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import PrintIcon from '@mui/icons-material/Print'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'

import { MapContainer, TileLayer, CircleMarker, Tooltip as LeafletTooltip } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'

import { tokens } from '../theme'
import {
  useEnterpriseSummary,
  type EnterpriseCompare,
  type EnterpriseKPI,
  type EnterprisePatch,
  type EnterprisePeriodKind,
  type EnterpriseSummary,
} from '../hooks/useEnterprise'

const SEV_COLOURS = {
  critical: tokens.alert,
  high:     '#E09935',
  warn:     tokens.gold,
  info:     tokens.textSec,
}
const TIER_COLOURS = {
  ok:   '#22886E',
  warn: '#E5A02D',
  bad:  tokens.alert,
}
const TIER_LABELS = { ok: 'On track', warn: 'Watch', bad: 'Critical' }

export default function EnterpriseDashboard() {
  const [period, setPeriod] = useState<EnterprisePeriodKind>('week')
  const [compare, setCompare] = useState<EnterpriseCompare>('prior')
  const [patchView, setPatchView] = useState<'grid' | 'table' | 'map'>('grid')
  const [snapshot, setSnapshot] = useState(false)

  const { data, isLoading, error } = useEnterpriseSummary(period, compare)

  return (
    <Container maxWidth="xl" sx={{ py: 3 }} className={snapshot ? 'snapshot-mode' : ''}>
      <Stack spacing={2}>
        <Header
          data={data}
          period={period}
          setPeriod={setPeriod}
          compare={compare}
          setCompare={setCompare}
          snapshot={snapshot}
          onSnapshot={() => { setSnapshot(true); setTimeout(() => window.print(), 350) }}
          onExitSnapshot={() => setSnapshot(false)}
        />

        {isLoading && (
          <Paper variant="outlined" sx={{ p: 4, display: 'grid', placeItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Paper>
        )}
        {error && (
          <Alert severity="error" variant="outlined">
            Enterprise summary failed to load — {String((error as Error)?.message ?? error)}
          </Alert>
        )}

        {data && (
          <>
            <KPIGrid data={data} />

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
              gap: 2,
            }}>
              <Stack spacing={2}>
                <VolumeChart data={data} />
                <FTFChart data={data} />
                <SeverityChart data={data} />
              </Stack>
              <Stack spacing={2}>
                <PatchesLeaderboard data={data} />
                <AssetsLeaderboard data={data} />
                <EngineersLeaderboard data={data} />
              </Stack>
            </Box>

            <PatchPerformance
              data={data}
              view={patchView}
              onView={setPatchView}
              snapshot={snapshot}
            />

            {snapshot && data.narratives?.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ fontSize: 14, fontWeight: 700, mb: 1, color: tokens.indigo }}>
                  Board narrative — recommended talking points
                </Typography>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 13.5, lineHeight: 1.6 }}>
                  {data.narratives.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              </Paper>
            )}
          </>
        )}
      </Stack>

      {/* Snapshot / print CSS. Applies to the whole page when either the
          user clicks the Snapshot button (screen render) or hits Ctrl+P
          (print render). */}
      <style>{`
        .snapshot-mode .no-snapshot { display: none !important; }
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; color: #000 !important; }
          .MuiPaper-root { box-shadow: none !important; border: 1px solid #ccc !important; page-break-inside: avoid; }
        }
      `}</style>
    </Container>
  )
}

// ---- Header --------------------------------------------------------------

function Header(props: {
  data: EnterpriseSummary | undefined
  period: EnterprisePeriodKind
  setPeriod: (p: EnterprisePeriodKind) => void
  compare: EnterpriseCompare
  setCompare: (c: EnterpriseCompare) => void
  snapshot: boolean
  onSnapshot: () => void
  onExitSnapshot: () => void
}) {
  const { data, period, setPeriod, compare, setCompare, snapshot, onSnapshot, onExitSnapshot } = props
  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: { md: 'flex-start' }, flexWrap: 'wrap' }}>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: tokens.indigo }}>
          Enterprise Dashboard
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
          {data?.period?.title ?? 'Loading summary…'}
        </Typography>
        {data && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
            Reporting <b style={{ color: tokens.charcoal }}>{data.period.range_label}</b>
            &nbsp;·&nbsp; Compared to <b style={{ color: tokens.charcoal }}>{data.period.prior_label}</b>
          </Typography>
        )}
      </Box>

      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }} className="no-snapshot">
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={(_, v: EnterprisePeriodKind | null) => v && setPeriod(v)}
          size="small"
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, fontWeight: 600 } }}
        >
          <ToggleButton value="day">Day</ToggleButton>
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
          <ToggleButton value="year">Year</ToggleButton>
        </ToggleButtonGroup>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="compare-lbl">Compare to</InputLabel>
          <Select
            labelId="compare-lbl"
            label="Compare to"
            value={compare}
            onChange={(e) => setCompare(e.target.value as EnterpriseCompare)}
          >
            <MenuItem value="prior">Prior period</MenuItem>
            <MenuItem value="ly">Same period, last year</MenuItem>
            <MenuItem value="baseline">28-day baseline</MenuItem>
          </Select>
        </FormControl>

        {snapshot ? (
          <Button variant="outlined" size="small" onClick={onExitSnapshot}>Exit snapshot</Button>
        ) : (
          <Button
            variant="contained"
            size="small"
            startIcon={<PrintIcon />}
            onClick={onSnapshot}
            sx={{ bgcolor: tokens.indigo, '&:hover': { bgcolor: tokens.indigoDark } }}
          >
            Snapshot for board
          </Button>
        )}
      </Stack>
    </Stack>
  )
}

// ---- KPI grid ------------------------------------------------------------

function KPIGrid({ data }: { data: EnterpriseSummary }) {
  const tiles: { key: keyof EnterpriseSummary['kpis']; label: string; icon: string }[] = [
    { key: 'completed', label: 'Jobs completed',    icon: '✓' },
    { key: 'ftf',       label: 'First-Time-Fix',    icon: '⇧' },
    { key: 'sla',       label: 'SLA on-time',       icon: '⏱' },
    { key: 'repeat',    label: 'Repeat visits',     icon: '↻' },
    { key: 'criticals', label: 'Open criticals',    icon: '!' },
    { key: 'raised',    label: 'Exceptions raised', icon: '⚑' },
  ]
  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
      gap: 1.5,
    }}>
      {tiles.map(t => (
        <KPITile key={t.key} label={t.label} icon={t.icon} k={data.kpis[t.key]} />
      ))}
    </Box>
  )
}

function KPITile({ label, icon, k }: { label: string; icon: string; k: EnterpriseKPI }) {
  const positive = k.kind === 'up-good' || k.kind === 'down-good'
  const negative = k.kind === 'up-bad' || k.kind === 'down-bad'
  const sparkColour = positive ? '#22886E' : negative ? tokens.alert : tokens.indigo
  const chipColour  = positive ? '#22886E' : negative ? tokens.alert : tokens.textSec
  const chipBg      = positive
    ? alpha('#22886E', 0.14)
    : negative
      ? alpha(tokens.alert, 0.14)
      : alpha(tokens.textSec, 0.12)
  const arrow =
    k.kind === 'up-good' || k.kind === 'up-bad'   ? '▲' :
    k.kind === 'down-good' || k.kind === 'down-bad' ? '▼' : '·'
  const deltaLabel = k.abs_label
    ?? ((k.delta > 0 ? '+' : (k.delta < 0 ? '' : '±')) + Math.abs(k.delta).toFixed(1) + '%')

  return (
    <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Box sx={{
          width: 22, height: 22, borderRadius: 1,
          display: 'grid', placeItems: 'center',
          bgcolor: alpha(tokens.indigo, 0.12), color: tokens.indigo,
          fontSize: 12, fontWeight: 700,
        }}>{icon}</Box>
        <Typography sx={{
          fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em',
          textTransform: 'uppercase', color: 'text.secondary',
        }}>{label}</Typography>
      </Stack>
      <Typography sx={{
        fontSize: 24, fontWeight: 700, color: tokens.indigo, letterSpacing: '-0.02em',
        fontVariantNumeric: 'tabular-nums', lineHeight: 1.1,
      }}>
        {formatNum(k.value)}<span style={{ fontSize: 14, fontWeight: 600, color: tokens.textSec, marginLeft: 2 }}>{k.suffix ?? ''}</span>
      </Typography>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5,
          fontSize: 11.5, fontWeight: 600,
          padding: '2px 7px', borderRadius: 999,
          bgcolor: chipBg, color: chipColour,
          fontVariantNumeric: 'tabular-nums', lineHeight: 1.4,
        }}>{arrow} {deltaLabel}</Box>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>vs prior</Typography>
      </Stack>
      <Spark values={k.spark} colour={sparkColour} />
    </Paper>
  )
}

// ---- Charts (inline SVG) -------------------------------------------------

function Spark({ values, colour }: { values: number[]; colour: string }) {
  if (!values || values.length < 2) return <Box sx={{ height: 32 }} />
  const w = 200, h = 32
  const min = Math.min(...values), max = Math.max(...values)
  const range = max - min || 1
  const step = w / (values.length - 1)
  const pts = values.map((v, i) => [i * step, (1 - (v - min) / range) * (h - 2) + 1])
  const path = pts.map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ')
  const area = path + ` L ${pts[pts.length - 1][0].toFixed(1)},${h} L 0,${h} Z`
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: 32, marginTop: 4 }}>
      <path d={area} fill={colour} fillOpacity={0.14} />
      <path d={path} fill="none" stroke={colour} strokeWidth={1.5} strokeLinejoin="round" />
    </svg>
  )
}

const CHART_W = 600, CHART_H = 160
const PAD = { l: 34, r: 12, t: 12, b: 24 }

function VolumeChart({ data }: { data: EnterpriseSummary }) {
  const bucketWord = data.period.bucket_label
  return (
    <ChartCard
      title="Volume"
      subtitle={`Jobs closed by outcome, per ${bucketWord}`}
      legend={[
        { swatch: '#22886E', label: 'Completed' },
        { swatch: tokens.alert, label: 'Failed' },
        { swatch: tokens.textSec, label: 'Cancelled' },
      ]}
    >
      <StackedBar
        series={{
          cancelled: data.trends.volume.cancelled,
          failed:    data.trends.volume.failed,
          completed: data.trends.volume.completed,
        }}
        colours={{
          cancelled: tokens.textSec,
          failed:    tokens.alert,
          completed: '#22886E',
        }}
        buckets={data.period.buckets}
      />
    </ChartCard>
  )
}

function FTFChart({ data }: { data: EnterpriseSummary }) {
  return (
    <ChartCard title="First-Time-Fix %" subtitle="Completed ÷ (completed + failed). Board target ≥ 80%.">
      <LineChart values={data.trends.ftf} baseline={80} buckets={data.period.buckets} min={60} max={100} />
    </ChartCard>
  )
}

function SeverityChart({ data }: { data: EnterpriseSummary }) {
  return (
    <ChartCard
      title="Exceptions raised by severity"
      subtitle='Every rule fires here — the "queue that would have interrupted a shift"'
      legend={[
        { swatch: tokens.alert, label: 'Critical' },
        { swatch: '#E09935',    label: 'High' },
        { swatch: tokens.gold,  label: 'Warn' },
        { swatch: tokens.textSec, label: 'Info' },
      ]}
    >
      <StackedBar
        series={{
          info:     data.trends.severity.info,
          warn:     data.trends.severity.warn,
          high:     data.trends.severity.high,
          critical: data.trends.severity.critical,
        }}
        colours={{
          info:     tokens.textSec,
          warn:     tokens.gold,
          high:     '#E09935',
          critical: tokens.alert,
        }}
        buckets={data.period.buckets}
      />
    </ChartCard>
  )
}

function ChartCard({
  title, subtitle, legend, children,
}: React.PropsWithChildren<{ title: string; subtitle?: string; legend?: { swatch: string; label: string }[] }>) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'baseline', flexWrap: 'wrap', mb: 1 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: tokens.indigo }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{subtitle}</Typography>}
        {legend && (
          <Stack direction="row" spacing={1.5} sx={{ ml: 'auto', flexWrap: 'wrap' }}>
            {legend.map(l => (
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }} key={l.label}>
                <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: l.swatch }} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 500, color: 'text.secondary' }}>{l.label}</Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
      {children}
    </Paper>
  )
}

function LineChart({ values, baseline, buckets, min, max }: {
  values: number[]
  baseline?: number
  buckets: string[]
  min: number
  max: number
}) {
  if (!values?.length) return <Box sx={{ height: 160 }} />
  const w = CHART_W - PAD.l - PAD.r
  const h = CHART_H - PAD.t - PAD.b
  const step = values.length > 1 ? w / (values.length - 1) : 0
  const xy = (v: number, i: number) => [PAD.l + i * step, PAD.t + (1 - (v - min) / (max - min)) * h] as const
  const pts = values.map(xy)
  const path = pts.map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ')
  const area = path + ` L ${pts[pts.length - 1][0].toFixed(1)},${(PAD.t + h).toFixed(1)} L ${pts[0][0].toFixed(1)},${(PAD.t + h).toFixed(1)} Z`
  const skip = Math.max(1, Math.ceil(buckets.length / 7))
  const baseY = baseline !== undefined ? PAD.t + (1 - (baseline - min) / (max - min)) * h : null
  return (
    <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: CHART_H }}>
      {[0, 1, 2, 3, 4].map(g => {
        const y = PAD.t + (g / 4) * h
        const val = max - (g / 4) * (max - min)
        return (
          <g key={g}>
            <line x1={PAD.l} y1={y} x2={PAD.l + w} y2={y} stroke={tokens.divider} strokeDasharray="2 3" />
            <text x={PAD.l - 6} y={y + 3} textAnchor="end" fontSize="10" fill={tokens.textSec}>{Math.round(val)}</text>
          </g>
        )
      })}
      {buckets.map((lab, i) => {
        if (i % skip !== 0 && i !== buckets.length - 1) return null
        const x = PAD.l + (i / (buckets.length - 1 || 1)) * w
        return <text key={i} x={x} y={CHART_H - 6} textAnchor="middle" fontSize="10" fill={tokens.textSec}>{lab}</text>
      })}
      {baseY !== null && (
        <>
          <line x1={PAD.l} y1={baseY} x2={PAD.l + w} y2={baseY} stroke={tokens.indigo} strokeWidth={1} strokeDasharray="4 4" opacity={0.6} />
          <text x={PAD.l + w - 2} y={baseY - 4} textAnchor="end" fontSize="10" fill={tokens.indigo}>Target {baseline}</text>
        </>
      )}
      <path d={area} fill={tokens.indigo} fillOpacity={0.10} />
      <path d={path} fill="none" stroke={tokens.indigo} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r={3.5} fill={tokens.indigo} />
    </svg>
  )
}

function StackedBar({ series, colours, buckets }: {
  series: Record<string, number[]>
  colours: Record<string, string>
  buckets: string[]
}) {
  const keys = Object.keys(series)
  const n = series[keys[0]]?.length ?? 0
  if (n === 0) return <Box sx={{ height: 160 }} />
  const totals = Array.from({ length: n }, (_, i) => keys.reduce((s, k) => s + (series[k][i] ?? 0), 0))
  const hi = Math.max(1, ...totals)
  const w = CHART_W - PAD.l - PAD.r
  const h = CHART_H - PAD.t - PAD.b
  const step = w / n
  const barW = Math.max(2, step * 0.72)
  const skip = Math.max(1, Math.ceil(buckets.length / 7))
  return (
    <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: CHART_H }}>
      {[0, 1, 2, 3, 4].map(g => {
        const y = PAD.t + (g / 4) * h
        const val = hi - (g / 4) * hi
        return (
          <g key={g}>
            <line x1={PAD.l} y1={y} x2={PAD.l + w} y2={y} stroke={tokens.divider} strokeDasharray="2 3" />
            <text x={PAD.l - 6} y={y + 3} textAnchor="end" fontSize="10" fill={tokens.textSec}>{Math.round(val).toLocaleString('en-GB')}</text>
          </g>
        )
      })}
      {Array.from({ length: n }, (_, i) => {
        const cx = PAD.l + i * step + step / 2
        let y = PAD.t + h
        return (
          <g key={i}>
            {keys.map(k => {
              const val = series[k][i] ?? 0
              const bh = (val / hi) * h
              y -= bh
              return <rect key={k} x={cx - barW / 2} y={y} width={barW} height={bh} fill={colours[k]} rx={1.5} />
            })}
          </g>
        )
      })}
      {buckets.map((lab, i) => {
        if (i % skip !== 0 && i !== buckets.length - 1) return null
        const x = PAD.l + i * step + step / 2
        return <text key={i} x={x} y={CHART_H - 6} textAnchor="middle" fontSize="10" fill={tokens.textSec}>{lab}</text>
      })}
    </svg>
  )
}

// ---- Leaderboards --------------------------------------------------------

function PatchesLeaderboard({ data }: { data: EnterpriseSummary }) {
  const rows = data.leaderboards.worst_patches ?? []
  return (
    <LeaderboardCard title="Worst patches by FTF" subtitle="Ranked by FTF (min 3 closed)">
      <Stack spacing={1}>
        {rows.length === 0 && <EmptyRow>No patch data for this window yet.</EmptyRow>}
        {rows.map((p, i) => (
          <LBRow
            key={p.patch}
            rank={i + 1}
            title={`${p.patch} · ${p.jobs} jobs`}
            meta={`${p.completed} complete · ${p.failed} failed · ${p.open} exc. open`}
            bar={{ value: p.ftf, tier: p.tier }}
            value={`${p.ftf.toFixed(1)}%`}
            valueMeta="FTF"
            valueColour={TIER_COLOURS[p.tier]}
          />
        ))}
      </Stack>
    </LeaderboardCard>
  )
}

function AssetsLeaderboard({ data }: { data: EnterpriseSummary }) {
  const rows = data.leaderboards.top_assets ?? []
  return (
    <LeaderboardCard title="Top repeat-fault assets" subtitle="Trailing 90 days">
      <Stack spacing={1}>
        {rows.length === 0 && <EmptyRow>No asset repeat data yet.</EmptyRow>}
        {rows.map((a, i) => (
          <LBRow
            key={a.asset_id}
            rank={i + 1}
            title={a.name || a.ref}
            meta={`${a.type} · trailing 90 days`}
            value={String(a.faults)}
            valueMeta="faults"
            valueColour={tokens.alert}
          />
        ))}
      </Stack>
    </LeaderboardCard>
  )
}

function EngineersLeaderboard({ data }: { data: EnterpriseSummary }) {
  const rows = data.leaderboards.engineers ?? []
  return (
    <LeaderboardCard title="Engineer FTF" subtitle="Worst 5 by FTF this period (min 3 closed)">
      <Stack spacing={1}>
        {rows.length === 0 && <EmptyRow>Not enough closed jobs for a leaderboard yet.</EmptyRow>}
        {rows.map((e, i) => {
          const arrow = e.dir === 'up' ? <TrendingUpIcon fontSize="inherit" /> : e.dir === 'down' ? <TrendingDownIcon fontSize="inherit" /> : <TrendingFlatIcon fontSize="inherit" />
          const bandTier: 'ok' | 'warn' | 'bad' = e.ftf >= 88 ? 'ok' : e.ftf >= 80 ? 'warn' : 'bad'
          return (
            <LBRow
              key={e.engineer_id}
              rank={i + 1}
              title={`${e.name} · ${e.external_ref}`}
              meta={
                <>
                  {e.jobs} closed &nbsp;·&nbsp;
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, verticalAlign: 'middle' }}>
                    {arrow} {e.delta > 0 ? '+' : ''}{e.delta.toFixed(1)}pp vs prior
                  </Box>
                </>
              }
              bar={{ value: e.ftf, tier: bandTier }}
              value={`${e.ftf.toFixed(1)}%`}
              valueMeta="FTF"
              valueColour={TIER_COLOURS[bandTier]}
            />
          )
        })}
      </Stack>
    </LeaderboardCard>
  )
}

function LeaderboardCard({ title, subtitle, children }: React.PropsWithChildren<{ title: string; subtitle?: string }>) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'baseline', flexWrap: 'wrap', mb: 1.5 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: tokens.indigo }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{subtitle}</Typography>}
      </Stack>
      {children}
    </Paper>
  )
}

function LBRow(props: {
  rank: number
  title: string
  meta?: React.ReactNode
  bar?: { value: number; tier: 'ok' | 'warn' | 'bad' }
  value: string
  valueMeta?: string
  valueColour?: string
}) {
  const { rank, title, meta, bar, value, valueMeta, valueColour } = props
  return (
    <Box sx={{
      display: 'grid', gridTemplateColumns: '20px 1fr auto', gap: 1.25, alignItems: 'center',
      p: 1, borderRadius: 1, bgcolor: alpha(tokens.indigo, 0.03),
      '&:hover': { outline: `1px solid ${alpha(tokens.indigo, 0.25)}` },
    }}>
      <Typography sx={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace', fontSize: 11, fontWeight: 600, color: 'text.secondary', textAlign: 'center' }}>
        {String(rank).padStart(2, '0')}
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.charcoal, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</Typography>
        {meta && <Typography component="div" sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>{meta}</Typography>}
        {bar && (
          <Box sx={{ height: 6, bgcolor: tokens.divider, borderRadius: 999, mt: 0.5, overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${bar.value}%`, bgcolor: TIER_COLOURS[bar.tier], borderRadius: 999 }} />
          </Box>
        )}
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: valueColour ?? tokens.indigo, letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
        {valueMeta && <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: 'text.secondary' }}>{valueMeta}</Typography>}
      </Box>
    </Box>
  )
}

function EmptyRow({ children }: React.PropsWithChildren) {
  return <Typography sx={{ fontSize: 12.5, color: 'text.secondary', textAlign: 'center', py: 2 }}>{children}</Typography>
}

// ---- Patch performance (grid / table / map) -----------------------------

function PatchPerformance({ data, view, onView, snapshot }: {
  data: EnterpriseSummary
  view: 'grid' | 'table' | 'map'
  onView: (v: 'grid' | 'table' | 'map') => void
  snapshot: boolean
}) {
  const sorted = useMemo(() => [...(data.patches ?? [])].sort((a, b) => a.ftf - b.ftf), [data.patches])
  const withCoords = useMemo(() => sorted.filter(p => p.lat != null && p.lng != null), [sorted])
  const bounds = useMemo(() => {
    if (withCoords.length === 0) return null
    const lats = withCoords.map(p => p.lat!), lngs = withCoords.map(p => p.lng!)
    return [
      [Math.min(...lats) - 0.05, Math.min(...lngs) - 0.05],
      [Math.max(...lats) + 0.05, Math.max(...lngs) + 0.05],
    ] as [[number, number], [number, number]]
  }, [withCoords])

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'baseline', flexWrap: 'wrap', mb: 1.5 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: tokens.indigo }}>Patch performance overview</Typography>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          Regional FTF at a glance — colour reflects tier band (≥88 good · 80–88 watch · &lt;80 critical)
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {!snapshot && (
          <Tabs
            value={view}
            onChange={(_, v: 'grid' | 'table' | 'map') => onView(v)}
            className="no-snapshot"
            sx={{ minHeight: 32, '& .MuiTab-root': { minHeight: 32, py: 0, textTransform: 'none', fontWeight: 600 } }}
          >
            <Tab value="grid" label="Grid" />
            <Tab value="table" label="Table" />
            <Tab value="map" label="Map" />
          </Tabs>
        )}
      </Stack>

      {view === 'grid' && <PatchGrid patches={sorted} />}
      {view === 'table' && <PatchTable patches={sorted} />}
      {view === 'map' && !bounds && (
        <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
          <Typography sx={{ fontSize: 13 }}>
            No patch coordinates available yet. Ensure premises records carry lat/lng so patch centroids can be computed.
          </Typography>
        </Box>
      )}
      {view === 'map' && bounds && (
        <Box sx={{ height: 380, borderRadius: 1, overflow: 'hidden', border: `1px solid ${tokens.divider}` }}>
          <MapContainer
            bounds={bounds}
            scrollWheelZoom={false}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {withCoords.map(p => (
              <CircleMarker
                key={p.patch}
                center={[p.lat!, p.lng!]}
                radius={Math.min(30, 8 + Math.sqrt(p.jobs))}
                pathOptions={{
                  color: TIER_COLOURS[p.tier],
                  fillColor: TIER_COLOURS[p.tier],
                  fillOpacity: 0.55,
                  weight: 2,
                }}
              >
                <LeafletTooltip direction="top">
                  <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700 }}>{p.patch}</div>
                  <div>FTF <b>{p.ftf.toFixed(1)}%</b> · {p.jobs} jobs · {p.open} open</div>
                </LeafletTooltip>
              </CircleMarker>
            ))}
          </MapContainer>
        </Box>
      )}
    </Paper>
  )
}

function PatchGrid({ patches }: { patches: EnterprisePatch[] }) {
  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
      gap: 0.75,
    }}>
      {patches.map(p => (
        <Box key={p.patch} sx={{
          bgcolor: '#fff', border: `1px solid ${tokens.divider}`, borderRadius: 1,
          p: 1, display: 'flex', flexDirection: 'column', gap: 0.5,
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 3,
            bgcolor: TIER_COLOURS[p.tier],
          }} />
          <Typography sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, fontWeight: 700, color: tokens.indigo, letterSpacing: '0.02em' }}>{p.patch}</Typography>
          <Typography sx={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em', color: tokens.charcoal, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
            {p.ftf.toFixed(1)}<span style={{ fontSize: 10.5, color: tokens.textSec, fontWeight: 500, marginLeft: 2 }}>%</span>
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
            {p.jobs} jobs · {p.open} open
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

function PatchTable({ patches }: { patches: EnterprisePatch[] }) {
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ '& td, & th': { fontVariantNumeric: 'tabular-nums' } }}>
        <TableHead>
          <TableRow>
            <TableCell>Patch</TableCell>
            <TableCell align="right">Jobs</TableCell>
            <TableCell align="right">Completed</TableCell>
            <TableCell align="right">Failed</TableCell>
            <TableCell align="right">FTF</TableCell>
            <TableCell align="right">Open exc.</TableCell>
            <TableCell>Severity mix</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {patches.map(p => {
            const total = p.mix.reduce((s, v) => s + v, 0) || 1
            return (
              <TableRow key={p.patch} hover>
                <TableCell><Box sx={{ fontFamily: 'JetBrains Mono, monospace', color: tokens.indigo, fontWeight: 600 }}>{p.patch}</Box></TableCell>
                <TableCell align="right">{p.jobs}</TableCell>
                <TableCell align="right">{p.completed}</TableCell>
                <TableCell align="right">{p.failed}</TableCell>
                <TableCell align="right"><b>{p.ftf.toFixed(1)}%</b></TableCell>
                <TableCell align="right">{p.open}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'inline-flex', height: 8, borderRadius: 999, overflow: 'hidden', width: 110, bgcolor: tokens.divider }}>
                    <Box sx={{ height: '100%', width: `${(p.mix[0] / total) * 100}%`, bgcolor: SEV_COLOURS.critical }} />
                    <Box sx={{ height: '100%', width: `${(p.mix[1] / total) * 100}%`, bgcolor: SEV_COLOURS.high }} />
                    <Box sx={{ height: '100%', width: `${(p.mix[2] / total) * 100}%`, bgcolor: SEV_COLOURS.warn }} />
                    <Box sx={{ height: '100%', width: `${(p.mix[3] / total) * 100}%`, bgcolor: SEV_COLOURS.info }} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{
                    display: 'inline-block', px: 1, py: 0.25, borderRadius: 0.5,
                    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
                    color: TIER_COLOURS[p.tier], bgcolor: alpha(TIER_COLOURS[p.tier], 0.14),
                  }}>{TIER_LABELS[p.tier]}</Box>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
}

// ---- Helpers -------------------------------------------------------------

function formatNum(v: number): string {
  if (v >= 10000) return v.toLocaleString('en-GB')
  if (v % 1 === 0) return String(v)
  return v.toFixed(1)
}
